import React, { useEffect, useMemo, useRef, useState } from 'react';
import { playDiceRollSound } from '../lib/diceEffects.js';

const pips = {
  1: [5],
  2: [1, 9],
  3: [1, 5, 9],
  4: [1, 3, 7, 9],
  5: [1, 3, 5, 7, 9],
  6: [1, 3, 4, 6, 7, 9]
};

const colors = ['red', 'yellow', 'green', 'blue'];
const colorLabels = {
  red: 'Roter Würfel',
  yellow: 'Gelber Würfel',
  green: 'Grüner Würfel',
  blue: 'Blauer Würfel'
};

function randomDie() {
  return Math.floor(Math.random() * 6) + 1;
}

function randomDice(lockedRows) {
  return {
    white1: randomDie(),
    white2: randomDie(),
    ...Object.fromEntries(colors.map((color) => [color, lockedRows[color] ? null : randomDie()]))
  };
}

function Die({ value, color = 'white', label, locked = false, rolling = false, landed = false, index = 0 }) {
  const className = [
    'die',
    `die--${color}`,
    locked ? 'is-locked' : '',
    rolling && !locked ? 'is-rolling' : '',
    landed && !locked ? 'has-landed' : ''
  ].filter(Boolean).join(' ');

  return (
    <div
      className={className}
      style={{ '--die-index': index }}
      aria-label={locked ? `${label} gesperrt` : rolling ? `${label} wird gewürfelt` : `${label}: ${value}`}
      aria-hidden={rolling ? 'true' : undefined}
    >
      {locked ? <span className="die__lock">×</span> : Array.from({ length: 9 }, (_, pipIndex) => (
        <i key={pipIndex} className={pips[value]?.includes(pipIndex + 1) ? 'is-on' : ''} />
      ))}
    </div>
  );
}

function EffectToggle({ label, checked, disabled = false, onChange }) {
  return (
    <label className={`effect-toggle ${disabled ? 'is-disabled' : ''}`}>
      <span>{label}</span>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}

export function DiceTray({ dice, lockedRows, whiteTotal, rolling, effects, onEffectsChange }) {
  const [displayDice, setDisplayDice] = useState(() => dice || randomDice(lockedRows));
  const [landed, setLanded] = useState(false);
  const wasRolling = useRef(false);
  const hapticsSupported = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

  useEffect(() => {
    if (!rolling || effects.reducedMotion) {
      if (dice) setDisplayDice(dice);
      return undefined;
    }

    setDisplayDice(randomDice(lockedRows));
    const interval = window.setInterval(() => setDisplayDice(randomDice(lockedRows)), 82);
    return () => window.clearInterval(interval);
  }, [rolling, dice, lockedRows, effects.reducedMotion]);

  useEffect(() => {
    if (wasRolling.current && !rolling && dice && !effects.reducedMotion) {
      setDisplayDice(dice);
      setLanded(true);
      const timer = window.setTimeout(() => setLanded(false), 520);
      wasRolling.current = rolling;
      return () => window.clearTimeout(timer);
    }
    wasRolling.current = rolling;
    return undefined;
  }, [rolling, dice, effects.reducedMotion]);

  const shownTotal = useMemo(() => {
    if (!displayDice) return null;
    return Number(displayDice.white1 || 0) + Number(displayDice.white2 || 0);
  }, [displayDice]);

  const updateEffect = (key, value) => onEffectsChange({ ...effects, [key]: value });

  if (!dice && !rolling) {
    return (
      <section className="dice-section dice-section--empty">
        <div className="dice-placeholder">Die Würfel warten auf den nächsten Wurf.</div>
        <EffectsMenu effects={effects} hapticsSupported={hapticsSupported} updateEffect={updateEffect} />
      </section>
    );
  }

  return (
    <section className={`dice-section ${rolling ? 'is-rolling' : ''}`} aria-label="Würfelergebnis">
      <div className={`white-total ${rolling ? 'is-rolling' : ''} ${landed ? 'has-landed' : ''}`}>
        <span>Weiße Summe</span>
        <strong aria-hidden={rolling ? 'true' : undefined}>{shownTotal ?? whiteTotal}</strong>
      </div>

      <div className="dice-tray" aria-hidden={rolling ? 'true' : undefined}>
        <Die value={displayDice.white1} label="Weißer Würfel 1" rolling={rolling && !effects.reducedMotion} landed={landed} index={0} />
        <Die value={displayDice.white2} label="Weißer Würfel 2" rolling={rolling && !effects.reducedMotion} landed={landed} index={1} />
        {colors.map((color, index) => (
          <Die
            key={color}
            value={displayDice[color]}
            color={color}
            label={colorLabels[color]}
            locked={Boolean(lockedRows[color])}
            rolling={rolling && !effects.reducedMotion}
            landed={landed}
            index={index + 2}
          />
        ))}
      </div>

      <EffectsMenu effects={effects} hapticsSupported={hapticsSupported} updateEffect={updateEffect} />

      <span className="sr-only" role="status" aria-live="polite">
        {rolling ? 'Die Würfel rollen.' : dice ? `Würfelergebnis: weiße Summe ${whiteTotal}.` : ''}
      </span>
    </section>
  );
}

function EffectsMenu({ effects, hapticsSupported, updateEffect }) {
  return (
    <details className="dice-effects">
      <summary title="Würfeleffekte einstellen" aria-label="Würfeleffekte einstellen"><span>⚙</span><b>Effekte</b></summary>
      <div className="dice-effects__panel">
        <strong>Würfeleffekte</strong>
        <EffectToggle label="Ton" checked={effects.sound} onChange={(value) => { updateEffect('sound', value); if (value) void playDiceRollSound(); }} />
        <EffectToggle label="Vibration" checked={effects.haptics} disabled={!hapticsSupported} onChange={(value) => updateEffect('haptics', value)} />
        <EffectToggle label="Weniger Bewegung" checked={effects.reducedMotion} onChange={(value) => updateEffect('reducedMotion', value)} />
        {!hapticsSupported && <small>Vibration wird von diesem Gerät nicht unterstützt.</small>}
      </div>
    </details>
  );
}
